import React, { useEffect, useState } from 'react';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import { FormGroup, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import AppInput from "components/Input/AppInput";
import { SHIFT, RATE_TYPE, DAY_TYPE, YEARS_OF_EXPERIENCE } from "config/AppConfig";
import AppButton from "components/CustomButtons/AppButton";
import { YupValidations } from 'utils/validate';
import MultiSelectField from "components/MultiSelect/MultiSelect";
import { useHistory, useParams } from 'react-router';
import SelectField from 'components/Select/SelectField';
import {
  fetchSpecialties,
  getSpecialties,
  fetchSupports,
  getSupports
} from "store/slices/worker";
import {
  fetchRateDetail,
  reduceCreateRate,
  reduceUpdateRate
} from "store/slices/rate";
import {
  fetchAccreditation
} from "store/slices/worker";

import { useDispatch, useSelector } from 'react-redux';
import { WORKER_TYPE } from 'config/AppConfig';

const useStyles = makeStyles(() => ({
  control: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginTop: '1rem'
  }
}));

const RateDetail = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { type: workerType, id: workerId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const [isNurse, setIsNurse] = useState(false);
  const [accreditations, setAccreditations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initializeData, setInitializeData] = useState({
    accreditationId: "",
    billToClient: "",
    dayOfWeek: "",
    description: "",
    name: "",
    ndisCode: "",
    payToStaff: "",
    rateType: "",
    shiftType: "",
    specialtyIds: [],
    specialtyId: "",
    supportId: "",
    workerType: "",
    yearsOfExperience: 1
  });

  const isAdd = window.location.pathname.includes('create');
  const supports = useSelector(getSupports) || [];
  const supportOptions = supports.map(item => {
    return {
      value: item.id,
      label: item.name
    }
  });
  const specialties = useSelector(getSpecialties) || [];
  const specialtyOptions = specialties.map(item => {
    return {
      value: item.id,
      label: item.name
    }
  });

  const _loadData = async (workerId) => {
    const result = await dispatch(fetchRateDetail({ id: workerId }));
    const { payload } = result;
    if (payload) {
      const data = { ...initializeData };
      Object.keys(initializeData)
        .forEach(key => {
          data[key] = payload[key] || "";
        });
      setInitializeData(data);
    }
  };
  const loadAccreditations = async (workerType) => {
    const result = await dispatch(fetchAccreditation({workerType: workerType}));
    if(result.payload) {
      setAccreditations(result.payload);
    }
  };

  const loadSpecialties = async () => {
    await dispatch(fetchSpecialties());
  };
  const loadSupports = async () => {
    await dispatch(fetchSupports());
  };

  useEffect(() => {
    if (workerType === WORKER_TYPE.SUPPORT_WORKER) {
      loadSupports();
    } else {
      loadSpecialties();
    }
    if (!isAdd && !!workerId) {
      _loadData(workerId);
    }
  }, [workerId]);
  useEffect(() => {
    if (workerType) {
      if (workerType === WORKER_TYPE.NURSE) {
        setIsNurse(true);
        loadAccreditations(WORKER_TYPE.NURSE);
      } else {
        setIsNurse(false);
        loadAccreditations(WORKER_TYPE.SUPPORT_WORKER);
      }
    }
  }, [workerType]);

  const handleSubmitForm = async (data) => {
    setIsLoading(true);
    let result;
    if (isAdd) {
      result = await dispatch(reduceCreateRate({ ...data }));
      result.payload && redirectListRate();
    } else {
      result = await dispatch(reduceUpdateRate({ ...data, id: workerId }));
    }
    setIsLoading(false);
  };

  const redirectListRate = () => {
      const url = `/admin/rates?workerType=${workerType === WORKER_TYPE.NURSE ? 'NURSE' : 'SUPPORT_WORKER'}`;
      history.push(url);
  };

  const validationSchema = Yup.object().shape({
    accreditationId: isNurse && Yup.string().required("validation.required"),
    billToClient: Yup.number().required('validation.required').min(1, { message: "validation.minValue", value: 1}),
    dayOfWeek: Yup.string().trim().required('validation.required'),
    name: Yup.string().trim().required('validation.required')
    .max(30, {
      message: 'validation.max_length',
      value: 30
    }),
    ndisCode: !isNurse && YupValidations.requiredString,
    payToStaff: Yup.number().required('validation.required').min(1, { message: "validation.minValue", value: 1}),
    rateType: Yup.string().trim().required('validation.required'),
    shiftType: Yup.string().trim().required('validation.required'),
    specialtyIds: isNurse && isAdd && Yup.array().required('validation.required'),
    specialtyId: isNurse && !isAdd && Yup.string().required('validation.required'),
    supportId: !isNurse && Yup.string().required('validation.required'),
    yearsOfExperience: isNurse && Yup.string().trim().required('validation.required')
  });

  return (
    <React.Fragment>
      <Formik
        initialValues={initializeData}
        validationSchema={validationSchema}
        onSubmit={(data) => {
          const {
            specialtyIds
          } = data;
          const params = {
            ...data,
            specialtyIds: specialtyIds ? specialtyIds.map(item => item.value) : [],
            workerType: workerType
          }
          handleSubmitForm(params);
        }}
        enableReinitialize
      >
        {() => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Field
                  component={AppInput}
                  name="name"
                  label={t('label.rateName')}
                  required
                />
                <Field
                  component={SelectField}
                  name="rateType"
                  label={"label.rateType"}
                  options={RATE_TYPE.map(item => {
                    return {
                      value: item,
                      label: t(`rateType.${item}`)
                    }
                  })}
                  required
                />
                <Field
                  component={SelectField}
                  name="dayOfWeek"
                  label={"label.dayOfWeek"}
                  options={DAY_TYPE.map(item => {
                    return {
                      value: item,
                      label: t(`dayType.${item}`)
                    }
                  })}
                  required
                />
                {
                  isNurse && (
                    <Field
                      component={SelectField}
                      name="shiftType"
                      label={"label.shift"}
                      options={SHIFT.map(item => {
                        return {
                          value: item,
                          label: t(`shift.${item}`)
                        }
                      })}
                      required
                    />
                  )
                }
                <Field
                  component={AppInput}
                  name="payToStaff"
                  label={t('label.payToStaff')}
                  required
                  type="number"
                  min={1}
                  // mask={MASK.PHONE}
                  // maskChar=""
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {
                  isNurse && (
                    <React.Fragment>
                      <Field
                        component={isAdd ? MultiSelectField : SelectField}
                        name={isAdd ? "specialtyIds" : "specialtyId"}
                        label={t("label.specialty")}
                        placeholder={t("label.specialty")}
                        options={specialtyOptions}
                        required
                      />
                      <Field
                        component={SelectField}
                        name="accreditationId"
                        label={t("label.accreditation")}
                        placeholder={t("label.accreditation")}
                        options={accreditations.map(item => {
                          return {
                            value: item.id,
                            label: item.name
                          }
                        })}
                        required
                      />
                      <Field
                        component={SelectField}
                        name="yearsOfExperience"
                        label={t('title.yearsOfExperience')}
                        options={YEARS_OF_EXPERIENCE.map(item => {
                          return {
                            value: item,
                            label: item === 10 ? t('label.10OrLarge') : item
                          }
                        })}
                        required
                      />
                    </React.Fragment>
                  )
                }
                {
                  !isNurse && (
                    <React.Fragment>
                      <Field
                        component={SelectField}
                        name="shiftType"
                        label={"label.shift"}
                        options={SHIFT.map(item => {
                          return {
                            value: item,
                            label: t(`shift.${item}`)
                          }
                        })}
                        required
                      />
                      <Field
                        component={SelectField}
                        name="supportId"
                        label={t("label.support")}
                        options={supportOptions}
                        required
                      />
                      <Field
                        component={AppInput}
                        name="ndisCode"
                        label={t('label.ndisCode')}
                        required
                      />
                    </React.Fragment>
                  )
                }
                <Field
                  component={AppInput}
                  name="billToClient"
                  label={t('label.billToParticipant')}
                  required
                  type="number"
                  min={1}
                  // mask={MASK.PHONE}
                  // maskChar=""
                />
              </Grid>
              <Grid item xs={12} md={12}>
                <Field
                  component={AppInput}
                  name="description"
                  label={t('label.description')}
                  type="textarea"
                />
              </Grid>
            </Grid>
            <FormGroup className={classes.control}>
              <AppButton
                label={`${t("button.back")}`}
                rounder
                mode="outlined"
                isLoading={isLoading}
                disabled={isLoading}
                onClick={redirectListRate}
              />
              <AppButton
                className='ml-3'
                label={`${t("button.save")}`}
                rounder
                type="submit"
                isLoading={isLoading}
                disabled={isLoading}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>
    </React.Fragment>
  )
}

export default RateDetail;
